import React, { useEffect, useState } from 'react';

/* =========================================================
   Reusable bento layout primitives for CaraLoom dashboards
   ========================================================= */

export const BentoGrid = ({ children, className = '' }) => (
  <div className={`grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-4 lg:gap-5 ${className}`}>
    {children}
  </div>
);

export const BentoTile = ({
  children,
  span = 'lg:col-span-4',
  rowSpan = '',
  className = '',
  hover = true,
  testId,
  onClick,
  style,
  id,
}) => {
  const hasCustomBg = /bg-(gradient|cn-primary|cn-accent|cn-secondary|\[)/.test(className);
  const defaultBg = hasCustomBg ? '' : 'bg-white dark:bg-[#0F172A]';
  return (
    <div
      id={id}
      onClick={onClick}
      data-testid={testId}
      style={style}
      className={`relative overflow-hidden cn-card ${hover ? 'cn-card-hover' : ''} ${defaultBg} border border-cn-border/70 dark:border-white/5 ${span} ${rowSpan} ${className} scroll-mt-24`}
    >
      {children}
    </div>
  );
};

/** Animated counter from 0 -> value */
export const Counter = ({ value, suffix = '', duration = 900, className = '' }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      setN(value);
      return;
    }
    let start = 0;
    const startTime = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min((t - startTime) / duration, 1);
      setN(Math.round(start + (value - start) * p));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <span className={className}>{n}{suffix}</span>;
};

/** Conic-ring percentage indicator (responsive) */
export const RingScore = ({ value = 0, size = 88, label, color = 'var(--cn-primary)', track = '#E6E4E0' }) => {
  const pct = Math.max(0, Math.min(100, value || 0));
  return (
    <div className="relative inline-flex flex-col items-center justify-center" style={{ width: size, height: size }}>
      <div
        className="rounded-full"
        style={{
          width: size,
          height: size,
          background: `conic-gradient(${color} ${pct * 3.6}deg, ${track} 0deg)`,
        }}
      />
      <div
        className="absolute rounded-full bg-white dark:bg-[#0F172A] flex flex-col items-center justify-center"
        style={{ width: size - 14, height: size - 14, top: 7, left: 7 }}
      >
        <span className="font-display font-extrabold text-cn-text dark:text-white text-xl leading-none">
          {Math.round(pct)}
        </span>
        {label && <span className="text-[10px] uppercase tracking-wider text-cn-text-muted mt-0.5">{label}</span>}
      </div>
    </div>
  );
};

/** Inline sparkline using SVG path */
export const Sparkline = ({ data = [], height = 40, stroke = 'var(--cn-primary)', fill = 'rgba(44,85,69,0.12)' }) => {
  if (!data.length) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const step = 100 / (data.length - 1 || 1);
  const points = data.map((d, i) => [i * step, 100 - ((d - min) / range) * 100]);
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(2)},${p[1].toFixed(2)}`).join(' ');
  const area = `${path} L100,100 L0,100 Z`;
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full" style={{ height }}>
      <path d={area} fill={fill} />
      <path d={path} fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

/** Live pulse dot */
export const LiveDot = ({ color = 'var(--cn-success)', label }) => (
  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-cn-text-muted">
    <span className="relative flex h-2 w-2">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" style={{ background: color }} />
      <span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: color }} />
    </span>
    {label}
  </span>
);

/** Photographic empty state */
export const PhotoEmptyState = ({ image, title, body, action, testId }) => (
  <div className="relative overflow-hidden rounded-2xl border border-cn-border/60 bg-white dark:bg-[#0F172A] p-0" data-testid={testId}>
    {image && (
      <div className="relative aspect-[16/7] w-full overflow-hidden">
        <img src={image} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-white/95 dark:from-[#0F172A]/95 via-white/40 dark:via-[#0F172A]/40 to-transparent" />
      </div>
    )}
    <div className="p-6 -mt-12 relative">
      <h3 className="cn-display text-xl text-cn-text dark:text-white">{title}</h3>
      {body && <p className="text-sm text-cn-text-muted mt-1 max-w-sm">{body}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  </div>
);

/** Eyebrow + title header within a bento tile */
export const TileHeader = ({ eyebrow, title, action, className = '' }) => (
  <div className={`flex items-start justify-between gap-3 ${className}`}>
    <div>
      {eyebrow && <p className="cn-eyebrow">{eyebrow}</p>}
      <h3 className="cn-display text-lg sm:text-xl text-cn-text dark:text-white mt-1">{title}</h3>
    </div>
    {action}
  </div>
);
